import React, { useEffect, useState } from 'react';
import { neonQuery } from '../services/api';

// Shared "Attributed Orders" panel — shows orders captured by the storefront
// attribution tag (cp_attrib cookie → utm-capture), filtered to one channel.
// This is true LAST-CLICK attribution: the order arrived directly from that
// channel's tagged link (Meta fbclid / Google gclid / Dotdigital email-sms utm).
// Used by Meta / Google / Email campaign pages so each shows only its own orders.

function money(n) {
  if (n == null) return '—';
  const v = parseFloat(String(n).replace(/[$,]/g, ''));
  if (isNaN(v)) return '—';
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US'); } catch { return '—'; }
}

const CHANNELS = {
  meta: {
    title: 'Attributed Orders — Meta',
    blurb: 'Orders whose final click came from a Facebook/Instagram ad (fbclid). True last-click — Meta’s own number is usually higher because it also counts view-through.',
    metric: { label: 'Meta clicks', field: 'meta_clicks' },
    clickCol: { header: 'fbclid', get: (a) => a.fbclid },
  },
  google: {
    title: 'Attributed Orders — Google',
    blurb: 'Orders whose final click came from a Google Ads link (gclid), captured at checkout. True last-click attribution.',
    metric: { label: 'Google clicks', field: 'google_clicks' },
    clickCol: { header: 'gclid', get: (a) => a.gclid },
  },
  email: {
    title: 'Attributed Orders — Email / SMS',
    blurb: 'Orders whose final click came from a Dotdigital email or SMS link, captured at checkout. True last-click attribution.',
    metric: { label: 'Campaigns', field: 'unique_campaigns' },
    clickCol: null,
  },
};

// Generic "Provider reports vs. we tracked" strip. comparison = { label, count,
// value, color, providerNote }. The gap is the provider's over-attribution.
function ComparisonStrip({ comparison, summary, windowLabel }) {
  const num = (n) => { const v = parseFloat(n); return isNaN(v) ? 0 : v; };
  const provider = num(comparison.count);
  const ours = parseInt((summary && summary.total_attributed) || 0, 10);
  const gap = Math.max(0, provider - ours);
  const pct = provider > 0 ? Math.round((gap / provider) * 100) : 0;
  const fmtNum = (v) => v.toLocaleString('en-US', { maximumFractionDigits: 1 });
  const cell = { flex: 1, padding: '14px 16px', textAlign: 'center' };
  const big = { fontSize: 28, fontWeight: 800, lineHeight: 1.1, fontFamily: 'Montserrat' };
  const lab = { fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 };
  const sub = { fontSize: 10.5, color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.35 };
  return (
    <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ background: 'var(--gray-50)', padding: '8px 14px', fontSize: 12, fontWeight: 700, color: 'var(--navy)', borderBottom: '1px solid var(--gray-200)' }}>
        {comparison.label} says vs. we tracked{windowLabel ? ` · ${windowLabel}` : ''}
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <div style={cell}>
          <div style={{ ...lab, color: comparison.color || '#1a73e8' }}>{comparison.label} reports</div>
          <div style={{ ...big, color: comparison.color || '#1a73e8' }}>{fmtNum(provider)}</div>
          <div style={sub}>{comparison.value != null ? `conversions · ${'$' + num(comparison.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'conversions'}<br />{comparison.providerNote || ''}</div>
        </div>
        <div style={{ flex: '0 0 44px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-500)', fontSize: 22 }}>›</div>
        <div style={{ ...cell, borderLeft: '1px solid var(--gray-100)', borderRight: '1px solid var(--gray-100)' }}>
          <div style={{ ...lab, color: '#1a8a4a' }}>We tracked (last-click)</div>
          <div style={{ ...big, color: '#1a8a4a' }}>{ours.toLocaleString()}</div>
          <div style={sub}>direct ad-click orders · {money(summary && summary.total_revenue)}<br />The honest number</div>
        </div>
        <div style={cell}>
          <div style={{ ...lab, color: 'var(--orange)' }}>Over-attribution gap</div>
          <div style={{ ...big, color: 'var(--orange)' }}>{fmtNum(gap)}{provider > 0 ? <span style={{ fontSize: 14, fontWeight: 700 }}> ({pct}%)</span> : null}</div>
          <div style={sub}>conversions {comparison.label} claims beyond a direct ad click</div>
        </div>
      </div>
      <div style={{ padding: '7px 14px', fontSize: 10.5, color: 'var(--gray-500)', borderTop: '1px solid var(--gray-100)', background: '#fcfcfd' }}>
        Windows are approximate and a gap is normal — {comparison.label}'s conversion count includes modeled / cross-device / assisted credit our strict last-click attribution doesn't.
      </div>
    </div>
  );
}

export default function AttributedOrders({ channel = 'meta', from = null, to = null, comparison = null, windowLabel = null }) {
  const cfg = CHANNELS[channel] || CHANNELS.meta;
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      neonQuery('attribution_list', { channel, from, to, limit: 200, offset: 0 }),
      neonQuery('attribution_summary', { channel, from, to }),
    ]).then(([list, sum]) => {
      if (!alive) return;
      setOrders(Array.isArray(list) ? list : []);
      setSummary(Array.isArray(sum) ? sum[0] : null);
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [channel, from, to]);

  const th = { textAlign: 'left', padding: '8px', fontWeight: 700, color: 'var(--gray-500)', fontSize: 11 };
  const td = { padding: '8px', fontSize: 11.5 };

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', margin: '0 0 4px' }}>{cfg.title}</h3>
      <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 12, maxWidth: 720, lineHeight: 1.5 }}>{cfg.blurb}</div>

      {comparison && summary && <ComparisonStrip comparison={comparison} summary={summary} windowLabel={windowLabel} />}

      {summary && (
        <div className="kpi-grid" style={{ marginBottom: 12 }}>
          <div className="kpi-card">
            <div className="kpi-label">Attributed Orders</div>
            <div className="kpi-value">{parseInt(summary.total_attributed || 0, 10).toLocaleString()}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Revenue</div>
            <div className="kpi-value">{money(summary.total_revenue)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">{cfg.metric.label}</div>
            <div className="kpi-value">{parseInt(summary[cfg.metric.field] || 0, 10).toLocaleString()}</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">Attributed Orders</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading…</div>}
        {!loading && orders.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>
            No orders attributed to this channel yet. Orders show here the moment a customer arrives through a tagged link and checks out.
          </div>
        )}
        {!loading && orders.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ ...th, padding: '8px 12px' }}>Order</th>
                  <th style={th}>Customer</th>
                  <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                  <th style={th}>Source</th>
                  <th style={th}>Medium</th>
                  <th style={th}>Campaign</th>
                  {cfg.clickCol && <th style={{ ...th, textAlign: 'center' }}>{cfg.clickCol.header}</th>}
                  <th style={{ ...th, padding: '8px 12px' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ ...td, padding: '8px 12px', fontWeight: 600 }}>#{a.bc_order_id}</td>
                    <td style={td}>{[a.ofirstname, a.olastname].filter(Boolean).join(' ') || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{a.orderamount ? money(a.orderamount) : '—'}</td>
                    <td style={td}>
                      {a.utm_source && <span style={{ background: '#e8f4fd', color: '#1a6fb0', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{a.utm_source}</span>}
                    </td>
                    <td style={td}>{a.utm_medium || '—'}</td>
                    <td style={td}>{a.utm_campaign || '—'}</td>
                    {cfg.clickCol && (
                      <td style={{ ...td, textAlign: 'center' }}>
                        {cfg.clickCol.get(a) ? <span style={{ color: 'var(--green, #1a8a4a)', fontWeight: 700 }}>Yes</span> : '—'}
                      </td>
                    )}
                    <td style={{ ...td, padding: '8px 12px' }}>{fmtDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
